export const getUserDocumentsSchema = {
  querystring: {
    type: "object",
    properties: {
      userId: { type: "number" },
      page: { type: "number" },
      limit: { type: "number" },
      eventId: { type: "number" },
    },
    required: ["userId", "eventId"],
  },
};

export const getUserDocumentByIdSchema = {
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
    required: ["id"],
  },
};

export const createPersonalLinkSchema = {
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
    required: ["id"],
  },
};

export const openPersonalLinkSchema = {
  params: {
    type: "object",
    properties: {
      token: { type: "string" },
    },
    required: ["token"],
  },
};

export const createUserDocumentSchema = {
  body: {
    type: "object",
    properties: {
      userId: { type: "number" },
      eventId: { type: "number" },
      fileKey: { type: "string" },
      fileName: { type: "string" },
      fileType: { type: "string" },
      metadata: { type: "object" },
    },
    required: ["userId", "fileKey", "fileName", "fileType"],
  },
};

export const deleteUserDocumentSchema = {
  body: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
    required: ["id"],
  },
};

export const web_uploadUserDocumentSchema = {
  type: "object",
  properties: {
    fileName: {
      type: "string",
      minLength: 1,
      description: "File name is required",
    },
    fileKey: { type: "string", minLength: 1, description: "File is required" },
    fileType: {
      type: "string",
      minLength: 1,
      description: "File type is required",
    },
    userId: { type: "integer", minimum: 1, description: "User ID is required" },
    eventId: { type: "integer", nullable: true },
  },
  required: ["fileName", "fileKey", "fileType", "userId"],
};

export const batchValidateUserDocumentsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
  body: {
    type: "object",
    properties: {
      file: {
        type: "object",
        properties: {
          data: { type: "string" },
          name: { type: "string" },
          type: {
            type: "string",
            enum: [
              "text/csv",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              "application/vnd.ms-excel",
            ],
          },
        },
        required: ["data", "name", "type"],
      },
      identifierColumn: {
        type: "string",
        minLength: 1,
        errorMessage: "Select identifier column name",
      },
      filenameColumn: {
        type: "string",
        minLength: 1,
        errorMessage: "Select filename column name",
      },
    },
    required: ["file", "identifierColumn", "filenameColumn"],
  },
};

export const getEventDistributionsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
};

export const createDistributionAnnouncementSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
  body: {
    type: "object",
    properties: {
      distributionId: { type: "number" },
    },
    required: ["distributionId"],
  },
};

export const deleteDistributionSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
  body: {
    type: "object",
    properties: {
      distributionId: { type: "number" },
    },
    required: ["distributionId"],
  },
};

export const sendDistributionEmailsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
  body: {
    type: "object",
    properties: {
      distributionId: { type: "number" },
    },
    required: ["distributionId"],
  },
};

const distributionEmailsParams = {
  type: "object",
  properties: {
    eventId: { type: "number" },
    distributionId: { type: "number" },
  },
  required: ["eventId", "distributionId"],
};

export const getDistributionEmailsSchema = {
  params: distributionEmailsParams,
  query: {
    type: "object",
    properties: {
      sort: { type: "string" },
      filter: { type: "string" },
      page: { type: "integer", minimum: 1, default: 1 },
      limit: { type: "integer", minimum: 1, maximum: 100, default: 10 },
    },
  },
};

export const getDistributionEmailStatsSchema = {
  params: distributionEmailsParams,
};

export const resendDistributionEmailSchema = {
  params: distributionEmailsParams,
  body: {
    type: "object",
    properties: {
      documentId: { type: "number" },
    },
    required: ["documentId"],
  },
};

export const updateDistributionEmailRecipientSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      distributionId: { type: "number" },
      documentId: { type: "number" },
    },
    required: ["eventId", "distributionId", "documentId"],
  },
  body: {
    type: "object",
    additionalProperties: false,
    properties: {
      email: { type: "string", format: "email" },
      firstName: { type: ["string", "null"] },
      lastName: { type: ["string", "null"] },
    },
    required: ["email"],
  },
};

export const batchUploadUserDocumentsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
  body: {
    type: "object",
    properties: {
      file: {
        type: "object",
        properties: {
          data: { type: "string" },
          name: { type: "string" },
          type: {
            type: "string",
            enum: ["application/zip", "application/x-zip-compressed"],
          },
        },
        required: ["data", "name", "type"],
      },
      manifest: {
        type: "array",
        minItems: 1,
        items: {
          type: "object",
          properties: {
            ticketId: { type: "number" },
            userId: { type: "number" },
            filename: { type: "string" },
            identifierValue: { type: "string" },
          },
          required: ["ticketId", "userId", "filename"],
        },
      },
      documentName: {
        type: "string",
        minLength: 1,
      },
      shouldCreateAnnouncement: {
        type: "boolean",
        default: false,
      },
      shouldEmailRecipients: {
        type: "boolean",
        default: false,
      },
    },
    required: ["file", "manifest", "documentName"],
  },
};
